import { WorkflowAction } from './WorkflowAction';

// The push's outcome, as the integration platform classifies it (OLIO-2846). The platform decides
// which workflow status to report alongside these; the split that matters here is presentational.
export const SYNC_CLASSIFICATION = {
  // The vendor took the patient.
  SUCCESS: 'success',
  // PCC only: a pending record was filed. NOT the same as a patient existing — the facility has to
  // accept it first, which is why the card must not say "Synced" for this one.
  CREATED: 'created',
  // Correct vendor answers to a well-formed request that a person should still look at. These come
  // back `completed` and present as flagged.
  DUPLICATE: 'duplicate',
  LOCKED: 'locked',
  MATCHED: 'matched',
  NO_MATCH: 'no_match',
  // The vendor or the transport refused. Whether this is fixable depends on `vendorMessage`.
  ERROR: 'error',
  // Pre-flight, from the platform's driver: the demographics did not pass validation, so nothing
  // was sent. Fixable by editing and resending.
  VALIDATION_FAILED: 'validation_failed',
  // Pre-flight: a configuration problem (no EmrConnection, unusable facility id). Not fixable by
  // the coordinator.
  CONFIG_ERROR: 'config_error',
} as const;

// The classifications whose meaning is "we did not create anything, and you can fix it here".
const RECOVERABLE = new Set<string>([SYNC_CLASSIFICATION.ERROR, SYNC_CLASSIFICATION.VALIDATION_FAILED]);

// The ONLY classifications that mean a record now exists at the vendor — the mirror of olio-api's
// SUCCESS_CLASSIFICATIONS, and for the same reason.
//
// `completed` is NOT the test. duplicate/locked/matched/no_match also arrive completed, and they
// carry the facility they were ATTEMPTED against, so keying "synced" on completion reads a refusal
// as a landed record. That is not a cosmetic slip: it makes the UI say a real patient exists in a
// real building and must be discharged there by hand, when MatrixCare support confirmed a duplicate
// creates no resident at all. Wrong in the one direction that sends someone hunting for a record
// that was never made.
//
// `created` counts: a PCC pending record genuinely exists and survives a facility change, even
// though no patient does yet — which is why isPendingAcceptance still refuses to say "Synced".
const LANDED_AT_VENDOR = new Set<string>([SYNC_CLASSIFICATION.SUCCESS, SYNC_CLASSIFICATION.CREATED]);

// Read shape is camelCase: the platform stores the result with snake_case keys and OliveBranch
// camelCases every response key on the way out. So `vendor_message` arrives as `vendorMessage` and
// `facility.group_id` as `facility.groupId`. (olio-api's own DispatchGuard reads the snake_case form,
// because it reads from the database rather than through the HTTP boundary — both are correct.)
export interface SyncFacility {
  externalId?: string | null;
  groupId?: string | null;
}

// An existing patient PCC's match returned (OLIO-2846). Name + DOB only — the platform never sends
// the matched record's SSN-like identifier, and PCC forbids surfacing patientId.
export interface MatchedPatient {
  firstName?: string | null;
  lastName?: string | null;
  dob?: string | null;
}

interface SyncResult {
  ehr?: string;
  classification?: string;
  facility?: SyncFacility;
  vendorMessage?: string | null;
  matrixcare?: { residentId?: string | null; medicalRecordNumber?: string | null; euid?: string | null };
  pcc?: {
    pendingPatientId?: string | null;
    matchedPatientCount?: number | null;
    statusCode?: number | null;
    // The patients PCC's match returned, name + DOB only (the platform drops the SSN). Present on a
    // `matched` result so the flagged card can name the existing record.
    matchedPatients?: MatchedPatient[];
  };
  syncRecordId?: string | null;
  completedAt?: string | null;
}

export const SYNC_RESULT_VALUE = 'sync_patient_to_ehr_result';

// Where a dispatch stages the demographics the coordinator confirmed on screen. Mirrors the api's
// SUBMITTED_DEMOGRAPHICS_KEY.
export const SUBMITTED_DEMOGRAPHICS = 'submitted_demographics';

/**
 * The Sync Patient to EHR action (OLIO-2846) — the push into the winning facility's MatrixCare or
 * PointClickCare.
 *
 * Reads the result off THIS instance's own outputs (design §6c), which is also where a pre-flight
 * validation failure lands before anything is committed. Two things about this kind are unlike the
 * others and shape everything below:
 *
 *   * It is POST-DECISION, and it runs on a LIVE run (design 010). Recording the outcome ends the
 *     competition but the run completes by exhaustion, which this action is the last thing standing
 *     in the way of — so the run is open, polling continues, and results still arrive. Every state
 *     here is derived from the action plus the workflow, with no local state, because the server is
 *     what moves this card and local state would start disagreeing with it.
 *   * `action_required` is its READY state. `pending` means the referral is UNDECIDED — the kind
 *     requires the decision's own outputs, so the evaluator cannot activate it before the outcome
 *     lands. (It was the ready state before 010, when the kind's `run` was a no-op; the card returns
 *     null there now.)
 */
export class SyncPatientToEhrAction extends WorkflowAction {
  private get result(): SyncResult {
    const value = this.outputFor(SYNC_RESULT_VALUE);
    return value && typeof value === 'object' ? (value as SyncResult) : {};
  }

  get classification(): string | undefined {
    return this.result.classification;
  }

  /**
   * The demographics the last dispatch sent, where they differ from the referral's own.
   *
   * Only ever THIS instance's, which is why it survives a fix-and-resend (same instance) and not a
   * retry after a hard failure (a fresh instance starts with no outputs). That asymmetry is
   * deliberate for now — the durable fix is giving these a declared value of their own rather than
   * living in per-attempt outputs.
   */
  get submittedDemographics(): Record<string, string> {
    const value = this.outputFor(SUBMITTED_DEMOGRAPHICS);
    if (!value || typeof value !== 'object') return {};

    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, v]) => typeof v === 'string' && v.trim() !== '')
        .map(([name, v]) => [name, v as string])
    );
  }

  // The vendor's own words. Load-bearing rather than decorative: it is the only thing that tells a
  // coordinator WHY a push was refused (a missing MRN, a rejected field), and the platform reports a
  // rejection carrying prose as action_required precisely so this survives — Resolution.errored would
  // have discarded it.
  get vendorMessage(): string | null {
    return this.result.vendorMessage ?? null;
  }

  // The building this push actually landed in. Compared against the referral's current
  // selected_facility_id to decide whether the sync is still current (AC 7): a coordinator who moves
  // the referral to a different building must be able to push there, and the API's guard makes the
  // same comparison server-side.
  get syncedFacilityGroupId(): string | null {
    return this.result.facility?.groupId ?? null;
  }

  // Did a record actually LAND at the vendor? The question every "where is this patient" statement
  // in the UI should ask, rather than "did the action complete" — see LANDED_AT_VENDOR. Shared by
  // both call sites on purpose: two independent derivations of "synced" is exactly how one of them
  // drifts back to keying on completion.
  get isSyncedSuccessfully(): boolean {
    return this.isCompleted && LANDED_AT_VENDOR.has(this.classification ?? '');
  }

  get syncedFacilityExternalId(): string | null {
    return this.result.facility?.externalId ?? null;
  }

  // What to show as proof the push landed. MatrixCare returns a resident; PCC returns only a PENDING
  // record, which is why the two are not interchangeable in the copy above this.
  get residentId(): string | null {
    return this.result.matrixcare?.residentId ?? null;
  }

  get pendingPatientId(): string | null {
    return this.result.pcc?.pendingPatientId ?? null;
  }

  // How many existing patients PCC's match returned (the `matched` review case). Falls back to the
  // length of matchedPatients when the count wasn't reported.
  get matchedPatientCount(): number {
    return this.result.pcc?.matchedPatientCount ?? this.matchedPatients.length;
  }

  // The existing patients PCC matched, name + DOB only — what the flagged card names so a coordinator
  // can recognize the record to reconcile in PCC.
  get matchedPatients(): MatchedPatient[] {
    return this.result.pcc?.matchedPatients ?? [];
  }

  // A PCC push that only filed a pending record — the facility must accept it before a patient
  // exists, so the card says "Queued for facility acceptance" rather than "Synced".
  get isPendingAcceptance(): boolean {
    return this.classification === SYNC_CLASSIFICATION.CREATED;
  }

  // Whether editing the demographics and resending is the way out. Drives the "Fix and resend"
  // affordance — as opposed to a plain Retry, which resends unchanged.
  get isRecoverable(): boolean {
    return RECOVERABLE.has(this.classification ?? '') && !!this.vendorMessage;
  }

  // Has a push settled at all? Distinguishes "never sent" from "sent and came back", which the raw
  // status cannot: an errored action with no result is a transport failure, and one WITH a result
  // carries something to show.
  get hasResult(): boolean {
    return !!this.classification;
  }
}
